import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ErrorAlert } from '@/components/common/ErrorAlert';

interface AddNoteFormProps {
  onSubmit: (noteText: string) => Promise<void>;
  isSubmitting: boolean;
  error?: string | null;
}

const MAX_LENGTH = 2000;

/**
 * Form for adding a new note
 * - Textarea with character count
 * - Validates max length
 * - Shows loading state during submission
 * - Clears form on success
 */
export function AddNoteForm({ onSubmit, isSubmitting, error }: AddNoteFormProps) {
  const [noteText, setNoteText] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!noteText.trim() || noteText.length > MAX_LENGTH) {
      return;
    }

    try {
      await onSubmit(noteText.trim());
      setNoteText(''); // Clear form on success
    } catch {
      // Error is handled by parent
    }
  };

  const isOverLimit = noteText.length > MAX_LENGTH;
  const isEmpty = !noteText.trim();

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <ErrorAlert error={error} />

      <div className="space-y-2">
        <Textarea
          placeholder="Add a note..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          disabled={isSubmitting}
          className="min-h-[100px] resize-none"
          aria-label="Note text"
        />
        <div className="flex items-center justify-between">
          <p
            className={`text-sm ${
              isOverLimit ? 'text-red-600 font-medium' : 'text-gray-500'
            }`}
          >
            {noteText.length} / {MAX_LENGTH}
          </p>
          <Button
            type="submit"
            disabled={isEmpty || isOverLimit || isSubmitting}
            size="sm"
          >
            {isSubmitting ? 'Adding...' : 'Add Note'}
          </Button>
        </div>
      </div>
    </form>
  );
}
